// Pure helpers for the patient-portal treatment card.
//
// IMPORTANT: this module is intentionally pure — no JSX, no React hooks, no
// window/cookie access — so the treatment-card business rules (fill-request
// selection, refill-count display, edit-lock gating, pending-edit help text) can
// be unit-tested in isolation. These were lifted verbatim out of the
// portalPrescriptionRefills.jsx renderer; the only change is the import site.
//
// See docs/patient-portal-treatment-card.md for the behavior these encode.

import { addDays, isFuture, isPast } from "date-fns";
import pluralize from "pluralize";

import {
  DB_STATUS_ACTIVE,
  DB_STATUS_CANCELED,
  DB_STATUS_PAUSED,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_READY,
  EMR_USER_ID_SYSTEM,
  FILL_PHARMACY_ID_GOGOMEDS,
  FILL_PHARMACY_ID_GOGOMEDS_WEST,
  FILL_PHARMACY_ID_TRUEPILL,
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_INACTIVE,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  RX_EXPIRATION_DAYS,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";

import {
  displayFriendlyDate,
  isNullDate,
  isPharmacyPickupOrderType,
  numRefillsRemaining,
  subscriptionPauseState,
} from "~/shared/utils/sharedUtils";

// uuid.Nil — the zero value of the patient's lockedByDoctorID UUID column
// ("not locked by anyone").
export const UUID_NIL = "00000000-0000-0000-0000-000000000000";

// Statuses that mean a fill request failed / was withdrawn — never "on the way".
export const FILL_REQUEST_DEAD_STATUSES = [
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_INACTIVE,
];

// Short description of what editing a pending visit can change, tailored to the visit type and
// whether it's a pharmacy-pickup or mail-order fulfillment. Only performance-anxiety (PA/PAF) and
// insomnia-initial (IN) visits show this; GA/GAF and insomnia follow-ups (INF) return "" (no text).
export function pendingEditHelpText(visit) {
  const isPickup = isPharmacyPickupOrderType(visit.orderType);
  let detail = "";
  switch (visit.visitType) {
    case VISIT_TYPE_INSOMNIA:
      detail = isPickup
        ? "Edit your plan, pharmacy, or billing address."
        : "Edit your plan, pharmacy, or shipping address.";
      break;
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      detail = isPickup
        ? "Edit your quantity, pharmacy, billing address, or switch to mail order."
        : "Edit your quantity, pharmacy, shipping speed, or shipping address.";
      break;
    default:
      // GA/GAF and INF (insomnia follow-up): no explanatory text.
      return "";
  }
  return `Want to change something? ${detail}`;
}

// patientLockedByDoctor reports whether the patient is currently locked by a REAL doctor (i.e. a
// clinical hold), as opposed to the transient system lock applied automatically during a
// pending-visit edit. Self-service edits (Edit Pending Visit / Change Pharmacy / Change Shipping
// Address) must be hidden while a real doctor holds the lock.
export function patientLockedByDoctor(patient) {
  const lockedUntil = patient?.lockedUntil;
  if (!lockedUntil || !isFuture(new Date(lockedUntil))) return false;
  // lockedByDoctorID is a non-null UUID column; uuid.Nil means "not locked by anyone".
  const lockedBy = patient?.lockedByDoctorID;
  return (
    !!lockedBy && lockedBy !== UUID_NIL && lockedBy !== EMR_USER_ID_SYSTEM
  );
}

// canEditPharmacy reports whether the patient may still change the pharmacy/shipping address on a
// visit: the visit must be in an editable status and the patient must not be locked by a doctor.
// Mirrors the legacy portal gating.
export function canEditPharmacy(visit, patient) {
  return (
    [
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
    ].includes(visit.status) && !patientLockedByDoctor(patient)
  );
}

// refillsRemainingText returns the human-readable "X refills remaining" string for a prescription,
// or "" when no refill count should surface (pharmacy pickup, expired/canceled, or a pharmacy that
// requires a follow-up visit).
export function refillsRemainingText(visit, prescription) {
  // Pharmacy-pickup orders don't surface a refill count.
  if (isPharmacyPickupOrderType(visit.orderType)) return "";

  const expired =
    isPast(addDays(new Date(prescription.createdAt), RX_EXPIRATION_DAYS)) ||
    prescription.refillsCanceled;
  if (expired) return "";

  let numRefills = numRefillsRemaining(
    prescription.refills,
    prescription.numRefillsFilled,
  );
  // Truepill / GoGoMeds fills require a follow-up visit → show as no refills.
  if (
    prescription.fillPharmacyID == FILL_PHARMACY_ID_TRUEPILL ||
    prescription.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS ||
    prescription.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS_WEST ||
    prescription.fillPharmacyID == ""
  ) {
    numRefills = 0;
  }

  return numRefills == 0
    ? "No refills remaining"
    : `${pluralize("refill", numRefills, true)} remaining`;
}

// Returns the shipping address to display for a mail-order visit. When a patient
// orders a refill they can pick a DIFFERENT address, which is captured on that
// refill's VisitOrderPayment (VOP) rather than on the visit itself — so prefer
// the address from the most recent VOP that has one. Falls back to the visit's
// own address only when no VOP carries an address (older orders, or before any
// payment row exists). Returns a {address1, address2, city, usState, zip} object.
export function shippingAddressForVisit(visit) {
  const vop = (visit.visitOrderPayments || [])
    .filter((p) => p && p.address1)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const src = vop || visit;
  return {
    address1: src.address1 || "",
    address2: src.address2 || "",
    city: src.city || "",
    usState: src.usState || "",
    zip: src.zip || "",
  };
}

// Formats a {address1, address2, city, state, zip} into display lines:
// "address1 address2" then "City, ST ZIP", skipping any empty piece.
export function addressLines({ address1, address2, city, state, zip }) {
  const lines = [];
  const street = [address1, address2].filter(Boolean).join(" ");
  if (street) lines.push(street);
  const cityState = [state, zip].filter(Boolean).join(" ");
  const cityLine = [city, cityState].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  return lines;
}

// Returns the visit's most recent SHIPPED fill request (one with a trackingURL),
// or null when none is available yet. trackingURL is only populated once a
// shipment goes out, so a non-empty value is itself the "shipment on the way"
// signal; we still skip canceled fill requests. The store returns fill requests
// in no guaranteed order, so pick the newest by createdAt.
export function latestShippedFillRequest(visit) {
  let best = null;
  (visit.fillRequests || []).forEach((fr) => {
    if (!fr || !fr.trackingURL) return;
    if (fr.status === FILL_REQUEST_STATUS_CANCELLED) return;
    if (!best || new Date(fr.createdAt) > new Date(best.createdAt)) {
      best = fr;
    }
  });
  return best;
}

// True when the visit has a mail-order fill request that's been placed and is
// still progressing toward shipment but doesn't have a tracking link yet —
// tracking can lag the fill request by a day or two. This drives the "Tracking
// link coming soon" note for the ORIGINAL order's shipment, mirroring what we
// already show for in-progress refills. Fill requests that already carry a
// tracking link (surfaced via latestShippedFillRequest) or are in a terminal
// failed/cancelled state are excluded.
export function hasFillRequestAwaitingTracking(visit) {
  return (visit.fillRequests || []).some(
    (fr) =>
      fr &&
      !fr.trackingURL &&
      !FILL_REQUEST_DEAD_STATUSES.includes(fr.status),
  );
}

// Go's zero time ("0001-01-01...") marshals to a year <= 1; treat that as "unset".
// A real (e.g. delivered) date has a present-day year.
function isRealDate(value) {
  if (!value) return false;
  const d = new Date(value);
  return !isNaN(d.getTime()) && d.getFullYear() > 1;
}

// Returns the visit's most recent in-progress REFILL fill request, or null.
// Unlike latestShippedFillRequest (which also covers the original order's
// shipment for the "Track shipment" link), this requires the fill request to be
// a refill (isRefill) and not in a failed/cancelled state. It does NOT require a
// trackingURL — a refill is surfaced as soon as it's ordered ("being processed")
// and upgrades to "on the way" once tracking appears (which can lag). A
// failed/cancelled refill (SE/E/ME/X/D/I) is treated as no refill in progress,
// so we don't surface a failed send to the patient. A delivered refill is likewise
// no longer "in progress" — once AfterShip reports delivery (deliveredAt is a real
// date) the "refill on the way" banner clears.
export function latestRefillInProgress(visit) {
  let best = null;
  (visit.fillRequests || []).forEach((fr) => {
    if (!fr || !fr.isRefill) return;
    if (FILL_REQUEST_DEAD_STATUSES.includes(fr.status)) return;
    if (isRealDate(fr.deliveredAt)) return;
    if (!best || new Date(fr.createdAt) > new Date(best.createdAt)) {
      best = fr;
    }
  });
  return best;
}

// Maps a fill request's doctorPrescription{1..4}ID to the visit's prescriptions,
// returning the de-duped medications (by name + form factor) that shipment
// covers. Falls back to the visit's de-duped prescriptions when the fill request
// has no resolvable prescription IDs (older fill requests may not populate them).
export function fillRequestMedications(visit, fr) {
  const ids = [
    fr?.doctorPrescription1ID,
    fr?.doctorPrescription2ID,
    fr?.doctorPrescription3ID,
    fr?.doctorPrescription4ID,
  ].filter((id) => id && id !== UUID_NIL);

  // Resolve against the history list (active + canceled) so a shipment whose
  // prescription was later canceled still shows its medication name. Fall back to
  // the active-only list for payloads that predate allVisitPrescriptions.
  const historyRxs =
    visit.allVisitPrescriptions && visit.allVisitPrescriptions.length
      ? visit.allVisitPrescriptions
      : visit.visitPrescriptions || [];

  const rxById = {};
  historyRxs.forEach((rx) => {
    if (rx) rxById[rx.id] = rx;
  });

  const deDupeByMed = (rxs) => {
    const byMed = {};
    rxs.forEach((rx) => {
      const key = rx.medicationName + rx.medicationFormFactor;
      if (!byMed[key]) byMed[key] = rx;
    });
    return Object.values(byMed);
  };

  const matched = ids.map((id) => rxById[id]).filter(Boolean);
  if (matched.length === 0) return deDupeByMed(historyRxs);
  return deDupeByMed(matched);
}

// subscriptionPauseState moved to sharedUtils so the EMR side panel shares the same
// scheduled-vs-paused semantics; re-exported here to keep this module's public API stable.
export { subscriptionPauseState };

// subscriptionStatusChip returns the at-a-glance status pill for a subscription card:
// { label, tone } where tone is one of "active" | "warning" | "danger" | "neutral" (the
// renderer maps tone → color). It consolidates the state that used to be scattered as prose
// under the membership title. A scheduled (future) cancel is surfaced as "Cancels {date}" and
// takes precedence over the paused/active label, mirroring the old red "Canceled" badge which
// showed whenever cancelAt/canceledAt were set regardless of paused state. A paused
// subscription splits into "Active until {paidThroughAt}" while still inside the paid period vs
// "Paused · resumes {pausedUntil}" once the pause is functionally in effect (see
// subscriptionPauseState). Returns an empty label (render nothing) for states with no
// meaningful chip.
export function subscriptionStatusChip(subscription, now = new Date()) {
  if (!subscription) return { label: "", tone: "" };

  if (subscription.status === DB_STATUS_CANCELED) {
    return { label: "Canceled", tone: "neutral" };
  }

  // A pending or completed cancellation is reflected even while the sub is still technically
  // active/paused (end-of-period cancel) — a future cancelAt shows the date, anything else
  // (canceledAt set, or a cancelAt already in the past) reads as plain "Canceled".
  const hasCancelSchedule =
    !isNullDate(subscription.cancelAt) || !isNullDate(subscription.canceledAt);
  if (hasCancelSchedule) {
    if (
      !isNullDate(subscription.cancelAt) &&
      isFuture(new Date(subscription.cancelAt))
    ) {
      return {
        label: `Cancels ${displayFriendlyDate(subscription.cancelAt)}`,
        tone: "danger",
      };
    }
    return { label: "Canceled", tone: "neutral" };
  }

  // A pause that hasn't functionally started yet (still inside the paid period) reads as still
  // active — "Active until {date}" — not as already paused; once the paid period lapses it
  // becomes the plain paused label. See subscriptionPauseState.
  if (subscriptionPauseState(subscription, now) === "scheduled") {
    return {
      label: `Active until ${displayFriendlyDate(subscription.paidThroughAt)}`,
      tone: "warning",
    };
  }
  if (subscription.status === DB_STATUS_PAUSED) {
    return {
      label: isNullDate(subscription.pausedUntil)
        ? "Paused"
        : `Paused · resumes ${displayFriendlyDate(subscription.pausedUntil)}`,
      tone: "warning",
    };
  }

  // A plainly active subscription shows no chip — "active" is the implied
  // default, so the green "Active" pill was redundant noise beside the title.
  // Only the non-default states above (paused / cancel-scheduled / canceled)
  // surface a chip.
  return { label: "", tone: "" };
}

// A shipped fill request with no Delivered webhook after this long is treated as delivered
// anyway (webhooks occasionally go missing), so the next-shipment line isn't suppressed forever.
const DELIVERY_ASSUMED_AFTER_DAYS = 10;

// fillRequestInFlight reports whether a fill request is a shipment still making its way to the
// patient: live (not canceled/failed), not AfterShip-stamped as delivered (deliveredAt — see
// StampSubscriptionFillRequestDeliveries server-side), and not shipped so long ago that delivery
// is safe to assume. A fill that hasn't even shipped yet ("Preparing") counts as in flight.
function fillRequestInFlight(fr, now) {
  if (!fr || FILL_REQUEST_DEAD_STATUSES.includes(fr.status)) return false;
  if (isRealDate(fr.deliveredAt)) return false;
  // Assume delivery once enough time has passed — from the ship time when we have it, otherwise
  // from creation: subscription fills don't reliably populate completedAt, and one with a nil
  // visit_order_payment_id can never be matched to a Delivered webhook, so without this fallback
  // such a shipment would suppress the next-shipment line forever.
  const clock = isRealDate(fr.completedAt) ? fr.completedAt : fr.createdAt;
  return !(
    isRealDate(clock) &&
    now - new Date(clock) > DELIVERY_ASSUMED_AFTER_DAYS * 24 * 60 * 60 * 1000
  );
}

function rxExpiresAt(rx) {
  return addDays(new Date(rx.createdAt), RX_EXPIRATION_DAYS);
}

// subscriptionNextShipment returns the card-level "Next shipment" line for a subscription:
// { label, tone } with tone "neutral" (informational) or "warning" (a follow-up visit is needed
// for shipments to continue), or null when no line should show.
//
// The prediction mirrors how fills actually dispatch: autoship fills are triggered by the PAID
// renewal invoice for prescriptions with autoship_status='A' and refills remaining, so the next
// shipment is the next billing event —
//   - active: the server-maintained autoshipNextShipDate (recomputed onto the billing boundary
//     after every fill and on resume); earliest across the sub's autoship rxs. Before the FIRST
//     fill completes that field is still unset (the post-fill bookkeeping stamps it), so fall
//     back to the subscription's periodEndAt — the renewal that will dispatch the next fill.
//   - paused: billing is held until pausedUntil, so that IS the next shipment date (the server
//     clears each rx's autoshipNextShipDate for the duration of the pause).
//   - scheduled cancel: the renewal invoice will never be paid → no further shipments → null
//     (the status chip already shows "Cancels {date}").
//   - past_due/unpaid/canceled/etc.: no predictable shipment → null.
// A pharmacy won't fill an expired prescription, so if every autoship rx will already be expired
// on the computed ship date the line becomes a "prescription expires/expired — complete a
// follow-up visit" warning instead of promising a shipment that won't happen.
//
// And while a shipment is still in flight (preparing/sent/shipped but not yet delivered), the
// next-shipment promise is suppressed entirely — "Next shipment Jul 10" beside "Preparing your
// shipment" reads as a contradiction. Delivery is known from the AfterShip-stamped deliveredAt,
// with a shipped-long-ago fallback for missing webhooks (see fillRequestInFlight).
export function subscriptionNextShipment(subscription, now = new Date()) {
  if (!subscription) return null;

  // Same gate as the server's renewal-fill query: active autoship, refills left, not canceled
  // by the doctor. Anything else (trial meds, exhausted autoship) has no scheduled shipment.
  const autoshipRxs = (subscription.prescriptions || []).filter(
    (rx) =>
      rx.autoshipStatus === DB_STATUS_ACTIVE &&
      !rx.refillsCanceled &&
      rx.refills > rx.numRefillsFilled,
  );
  if (autoshipRxs.length === 0) return null;

  if (
    !isNullDate(subscription.cancelAt) &&
    new Date(subscription.cancelAt) > now
  ) {
    return null;
  }

  // Don't promise the NEXT box while the previous one is still on its way.
  if ((subscription.fillRequests || []).some((fr) => fillRequestInFlight(fr, now))) {
    return null;
  }

  let shipsAt = null;
  if (subscription.status === DB_STATUS_PAUSED) {
    if (isNullDate(subscription.pausedUntil)) return null;
    shipsAt = new Date(subscription.pausedUntil);
  } else if (subscription.status === DB_STATUS_ACTIVE) {
    autoshipRxs.forEach((rx) => {
      if (isNullDate(rx.autoshipNextShipDate)) return;
      const candidate = new Date(rx.autoshipNextShipDate);
      if (!shipsAt || candidate < shipsAt) shipsAt = candidate;
    });
    if (!shipsAt && !isNullDate(subscription.periodEndAt)) {
      shipsAt = new Date(subscription.periodEndAt);
    }
  } else {
    return null;
  }
  // A ship date in the past is stale (e.g. a renewal payment being retried) — show nothing
  // rather than a date that already went by.
  if (!shipsAt || shipsAt <= now) return null;

  if (autoshipRxs.some((rx) => rxExpiresAt(rx) > shipsAt)) {
    return {
      label: `Next shipment ${displayFriendlyDate(shipsAt)}`,
      tone: "neutral",
    };
  }

  // Every autoship rx will be expired by the time the next fill would go out. Surface when the
  // LAST one dies (until then a follow-up can still be framed as "continue", not "restart").
  const lastExpiry = autoshipRxs
    .map(rxExpiresAt)
    .reduce((latest, d) => (d > latest ? d : latest));
  return {
    label:
      lastExpiry <= now
        ? "Prescription expired — complete a follow-up visit to continue shipments"
        : `Prescription expires ${displayFriendlyDate(
            lastExpiry,
          )} — complete a follow-up visit to continue shipments`,
    tone: "warning",
  };
}

// subscriptionShipmentStatus returns the current shipment status line for a prescription:
// { label, trackingURL } where a non-empty trackingURL means the renderer should append a
// "Track shipment" link. It expects an rxWithFill as produced by prescriptionWithFillRequests()
// — i.e. with a `mostRecentFillRequest` that already excludes dead (canceled/errored) fill
// requests. Progression: shipped (with tracking) → sent (filled, tracking lagging) → preparing
// (nothing shipped yet). Replaces the old bare "Pending shipment" / "Last sent".
//
// Once the shipment is delivered the live status (and its tracking link) is stale, so the line
// flips to history: "Most recent shipment {ship date}". Delivery is judged by the same rule
// that un-suppresses the card's next-shipment line (fillRequestInFlight): the AfterShip-stamped
// deliveredAt (StampSubscriptionFillRequestDeliveries server-side), or shipped/created long
// enough ago that delivery is safe to assume — so this line and "Next shipment" can never
// contradict each other.
export function subscriptionShipmentStatus(rxWithFill, now = new Date()) {
  const fr = rxWithFill?.mostRecentFillRequest;

  // Delivered (or assumed delivered): report the shipment as history, no tracking link.
  // The { createdAt: 0 } sentinel seeded when a prescription has no live fill requests
  // never reaches this branch — fillRequestInFlight treats a fill with no real dates as
  // still in flight — so it falls through to "Preparing your shipment" below.
  if (fr && !fillRequestInFlight(fr, now)) {
    const sentOn = isRealDate(fr.completedAt) ? fr.completedAt : fr.createdAt;
    return {
      label: `Most recent shipment ${displayFriendlyDate(sentOn)}`,
      trackingURL: "",
    };
  }

  // A tracking URL is only populated once a shipment actually goes out, so it's the
  // "on the way" signal. Prefer completedAt for the ship date, falling back to createdAt.
  if (fr && fr.trackingURL) {
    const sentOn = isRealDate(fr.completedAt) ? fr.completedAt : fr.createdAt;
    return {
      label: isRealDate(sentOn)
        ? `Shipped ${displayFriendlyDate(sentOn)}`
        : "Shipped",
      trackingURL: fr.trackingURL,
    };
  }

  // Filled/sent but tracking hasn't landed yet (it can lag a day or two).
  if (fr && isRealDate(fr.completedAt)) {
    return { label: `Sent ${displayFriendlyDate(fr.completedAt)}`, trackingURL: "" };
  }

  return { label: "Preparing your shipment", trackingURL: "" };
}
